import theme from '@app/ui/theme'
import styled from 'styled-components'

export const Wrapper = styled.div`
  background-color: ${theme.colors.background};
  /* No font family here on purpose: the labels, descriptions and tee cards
     take the one the body sets, which is what the preparation page's fields
     are in. The headings ask for Roboto themselves. */
  /* The page fills the area the navigator gives it rather than the whole
     window. A full viewport height is taller than that area, which pushed the
     foot of the page past the bottom edge where it could not be scrolled to. */
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`

// The card the preparation page is made of, so the two pages stack the same
// way: one card per part of the form.
export const ContentCard = styled.div`
  margin: 20px 24px;
  box-shadow: 0 0px 4px 0 ${theme.colors.dark1};
  background-color: ${theme.colors.white};
  padding: 30px 40px;
  border-radius: 4px;
`

export const ContentHeading = styled.h2`
  margin: 0;
  padding: 0;
  font-family: 'Roboto', sans-serif;
  font-size: 23px;
  font-weight: 460;
  letter-spacing: 0.4px;
  color: #242c2b;
`

export const ContentSeparator = styled.div`
  margin-top: 14px;
  margin-bottom: 25px;
  height: 1px;
  background-color: ${theme.colors.light};
`

// The heading sits against the top of the preview rather than its middle, so
// the preview can grow downwards as the form fills in.
export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
`

// The heading and its description share the row with the preview, which
// cannot be made narrower than the table it shows. The text column takes
// whatever is left over and wraps inside it, so a narrow window reflows the
// description instead of dropping the preview below it.
export const CardHeaderText = styled.div`
  flex: 1 1 240px;
  min-width: 0;
`

export const Subtitle = styled.p`
  margin: 8px 0 0 0;
  padding: 0;
  font-size: 14px;
  font-weight: 400;
  color: #666;
`

// The width every control on the page is given, so the fields line up in one
// column however they are built.
export const FIELD_WIDTH = '300px'

// A pair of buttons standing in for a field. They split the width of the
// fields above them rather than carrying a width of their own, so the column
// keeps one right edge: the button adds its own padding to whatever width it
// is given, which fixed widths cannot account for.
export const ButtonPair = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: ${FIELD_WIDTH};

  & > * {
    flex: 1;
  }
`

// The tee cards and the two ways of adding one, lined up the way the
// preparation page lines its tees up, wrapping onto a second row when a full
// set of tees runs out of width.
export const TeeContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-end;
  align-items: stretch;
  gap: 8px;
`

export const AddTeeCard = styled.div<{ hasError?: boolean }>`
  width: 100px;
  min-height: 96px;
  box-sizing: border-box;
  padding: 8px;
  background-color: ${theme.colors.white};
  border: 1px dashed
    ${(props) => (props.hasError ? theme.colors.warning : '#cbd5e1')};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    border-color: ${theme.colors.secondary2};
  }
`

export const TeeAddButton = styled.div`
  background-color: ${theme.colors.secondary2};
  width: 28px;
  height: 28px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  display: flex;
  /* Doubled up on purpose: the form row this sits in styles every span it
     contains, and this has to be the rule that wins. */
  && > span {
    font-size: 20px;
    font-weight: 500;
    line-height: 1;
    color: ${theme.colors.white};
  }
`

export const TeeButtonTitle = styled.div`
  font-size: ${theme.fontSize[0]}px;
  font-weight: 700;
  line-height: 15px;
  color: #282828;
  text-align: center;
`

// The buttons close the same card the form is in, so they need the gap the
// card they used to have gave them.
export const BottomButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 30px;
`
